import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { KpiCard } from "@/components/common/KpiCard";
import { createExpense, fetchExpenses } from "@/api/finance";
import { fetchBranches } from "@/api/branches";
import { EXPENSE_KIND_LABELS } from "@/types/enums";
import type { ExpenseKind } from "@/types/enums";
import { formatDate, formatMoney } from "@/utils/format";
import { extractErrorMessage } from "@/types/common";

function ExpenseCreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const [branchId, setBranchId] = useState("");
  const [kind, setKind] = useState<ExpenseKind>("ADMIN");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [comment, setComment] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => createExpense({ branchId, kind, name, category, quantity, unitPrice, date, comment: comment || undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["expenses"] });
      onClose();
    },
    onError: (error) => setServerError(extractErrorMessage(error)),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Новый расход</DialogTitle>
      <DialogContent>
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={6}>
            <TextField select label="Филиал" fullWidth value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {(branches ?? []).map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Тип расхода" fullWidth value={kind} onChange={(e) => setKind(e.target.value as ExpenseKind)}>
              {Object.entries(EXPENSE_KIND_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField label="Название" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Категория" fullWidth value={category} onChange={(e) => setCategory(e.target.value)} />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Количество" type="number" fullWidth value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Цена за ед." type="number" fullWidth value={unitPrice} onChange={(e) => setUnitPrice(Number(e.target.value))} />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Дата" type="date" fullWidth value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Комментарий" fullWidth multiline minRows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <Alert severity="info">Сумма расхода: {formatMoney(quantity * unitPrice)}</Alert>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button variant="contained" disabled={!branchId || !name || !category || mutation.isPending} onClick={() => mutation.mutate()}>
          Добавить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ExpensesTab() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["expenses"],
    queryFn: () => fetchExpenses({ limit: 100 }),
  });

  const expenses = data?.items ?? [];
  const total = expenses.reduce((sum, e) => sum + e.amount, 0);
  const admin = expenses.filter((e) => e.kind === "ADMIN").reduce((sum, e) => sum + e.amount, 0);
  const house = expenses.filter((e) => e.kind === "HOUSE").reduce((sum, e) => sum + e.amount, 0);

  return (
    <>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={4}>
          <KpiCard label="Расходы за период" value={formatMoney(total)} hint={`${expenses.length} операций`} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard label="Административные" value={formatMoney(admin)} hint="аренда, реклама, налоги" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard label="Хозяйственные" value={formatMoney(house)} hint="канцелярия, вода, ремонт" />
        </Grid>
      </Grid>
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Добавить расход
        </Button>
      </Stack>
      <QueryState isLoading={isLoading} error={error}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Дата</TableCell>
              <TableCell>Филиал</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Название</TableCell>
              <TableCell>Категория</TableCell>
              <TableCell>Сумма</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.items ?? []).map((expense) => (
              <TableRow key={expense.id}>
                <TableCell>{formatDate(expense.date)}</TableCell>
                <TableCell>{expense.branchName ?? expense.branchId}</TableCell>
                <TableCell>
                  <StatusChip label={EXPENSE_KIND_LABELS[expense.kind]} />
                </TableCell>
                <TableCell>{expense.name}</TableCell>
                <TableCell>{expense.category}</TableCell>
                <TableCell>{formatMoney(expense.amount)}</TableCell>
              </TableRow>
            ))}
            {(data?.items ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Расходов пока нет.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </QueryState>
      <ExpenseCreateDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
}
